/**
 * 직원 명부 개편(2026-09-13 · H) — 목록 · 상세 · 입사 처리가 같이 쓰는 규칙.
 *
 * ★ 문구 · 검사 규칙은 종전 `AttendancePage/EmployeesPage.tsx` 와 **같은 값**이다(그 화면은 `/erp/hr/employees/legacy`
 *   로 그대로 남는다). 한쪽만 바뀌면 같은 서버 응답을 두 화면이 다르게 말한다 — 고칠 때는 둘 다 본다.
 */

export const EMPLOYEES_PATH = '/erp/hr/employees';
export const employeePath = (userId: string): string => `${EMPLOYEES_PATH}/${encodeURIComponent(userId)}`;
export const EMPLOYEE_NEW_PATH = `${EMPLOYEES_PATH}/new`;
/** 인사카드(인쇄용) — 2026-09-13 과제 J. */
export const employeeCardPath = (userId: string): string => `${employeePath(userId)}/card`;
/** 종전 통합 화면(계정 · 권한 · 부서 편집) — 새 상세에서 `?userId=` 로 그 사람을 연 채 보낸다(2026-09-13 · K). */
export const STAFF_PATH = '/erp/hr/staff';

export const ERRORS: Record<string, string> = {
  ACCOUNT_FIELD_NOT_HERE: '계정 칸은 관리 콘솔의 사용자 관리에서 고칩니다.',
  EMAIL_EXISTS: '이미 있는 이메일입니다.',
  EMAIL_EXISTS_DELETED: '지운 계정이 같은 이메일을 쓰고 있습니다 — 관리 콘솔에서 되살려 주세요.',
  EMAIL_INVALID: '이메일 형식이 아닙니다.',
  INVALID_DATE: '없는 날짜입니다.',
  WORKSITE_NOT_FOUND: '이 회사 사업장이 아닙니다 — 화면을 새로 고쳐 다시 골라 주세요.',
  FORBIDDEN: '이 화면은 회사 관리자만 쓸 수 있습니다.',
  FST_ERR_VALIDATION: '입력 형식이 올바르지 않습니다 — 이름은 비울 수 없고, 생일은 MM-DD 입니다.',
};

export function errorCode(err: unknown): string {
  return (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
}

/** 생일은 연도 없는 `MM-DD` — 서버 · 본인 경로와 같은 규칙. */
export const BIRTHDAY_RE = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

/** 재직 상태 → 배지 톤. 예정 상태(④)는 서버가 주면 같은 표에 더한다. */
export const STATUS_TONE: Record<string, 'ok' | 'warn' | 'slate' | 'bad' | 'pur'> = {
  ACTIVE: 'ok', LEAVE: 'warn', RESIGNED: 'slate',
  JOIN_PENDING: 'slate', TRANSFER_PENDING: 'pur', LEAVE_PENDING: 'warn', RESIGN_PENDING: 'bad',
};

export const STATUS_LABEL_EXTRA: Record<string, string> = {
  JOIN_PENDING: '입사 예정', TRANSFER_PENDING: '전보 예정', LEAVE_PENDING: '휴직 예정', RESIGN_PENDING: '퇴사 예정',
};

export function initialOf(name: string): string {
  return name.trim().slice(0, 1) || '?';
}

/* ★ 2026-09-13 직원관리 서버 칸(계약 §18.3~18.5) — 목록 · KPI · 상세가 같은 말을 쓴다. */
export const UPCOMING_HIRE_LABEL = '입사 예정';
/**
 * 입사 예정인가 — 서버 `?status=UPCOMING` 과 **같은 식**(재직 ∧ 입사일 > 오늘).
 * ★ 행 · 상세의 `upcomingHire` 는 상태와 무관하게 입사일만 본다 — 휴직 · 퇴직이면서 입사일이 미래인 사람을 입사 예정으로 세지 않게(리뷰 #1232).
 */
export function isUpcomingHire(r: { employmentStatus: string; upcomingHire?: boolean }): boolean {
  return r.employmentStatus === 'ACTIVE' && !!r.upcomingHire;
}
/* ★ 2026-09-14 계약 §18.4 「퇴사 예정 칸」 — 목록 줄 · 거르기 · KPI · 상세가 같은 말을 쓴다. */
export const UPCOMING_RESIGN_LABEL = STATUS_LABEL_EXTRA.RESIGN_PENDING ?? '퇴사 예정';
/** 퇴사일(마지막 근무일)이 지났는데 퇴사 처리가 아직 열려 있다(OPEN) — 관제 결정 문구. */
export const RESIGN_OVERDUE_LABEL = '처리 대기';
/**
 * 퇴사 배지 — 재직 상태 배지 **옆에 더한다**(재직 판정은 바뀌지 않는다 — 퇴사 예정자도 ACTIVE).
 * - `upcomingResign` → 「퇴사 예정」(RESIGN_PENDING 톤)
 * - 퇴사일은 있는데 예정이 아니다(날짜가 지남 · 아직 OPEN) → 「처리 대기」(warn)
 * - 칸이 없거나(옛 서버) 퇴사일이 없으면 `null` — 아무것도 그리지 않는다
 */
export function resignBadge(r: { resignDate?: string | null; upcomingResign?: boolean }):
  { tone: 'bad' | 'warn'; label: string; title: string } | null {
  if (r.upcomingResign) {
    return { tone: 'bad', label: UPCOMING_RESIGN_LABEL, title: r.resignDate ? `퇴사일 ${r.resignDate}(마지막 근무일)` : UPCOMING_RESIGN_LABEL };
  }
  if (r.resignDate) {
    return { tone: 'warn', label: RESIGN_OVERDUE_LABEL, title: `퇴사일 ${r.resignDate} 이 지났는데 퇴사 처리가 아직 열려 있습니다` };
  }
  return null;
}
/** 수습 종료일 라벨 — 관제 결정 문구. */
export const PROBATION_END_LABEL = '수습 종료(전환일)';
/** `probationMonthsSource === 'DEFAULT'` 일 때의 안내. */
export const probationDefaultNote = (months = 3): string => `회사 설정 전 기본 ${months}개월`;
/** 상위자 출처 — 사다리의 어느 칸에서 왔나. */
export const SUPERVISOR_SOURCE_LABEL: Record<string, string> = {
  TEAM_LEADER: '팀장', DIVISION_LEADER: '본부장', REPRESENTATIVE: '대표',
};
/** 오늘(UTC 날짜 — 서버 판정 기준과 같다)에 days 를 더한 `YYYY-MM-DD`. */
export function utcDatePlus(days: number, now: Date = new Date()): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + days)).toISOString().slice(0, 10);
}

/** 사진 규격 — 서버가 지키는 값과 같다(PersonHead 와 같은 규칙). */
export const AVATAR_MIME = ['image/png', 'image/jpeg', 'image/webp'];
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_ERRORS: Record<number, string> = {
  403: '사진을 바꿀 권한이 없습니다.',
  413: '이미지는 2MB 이하만 올릴 수 있습니다.',
  415: 'PNG · JPEG · WEBP 만 올릴 수 있습니다.',
  400: '이미지 형식이 올바르지 않습니다.',
  502: '이미지 저장에 실패했습니다 — 잠시 뒤 다시 시도해 주세요.',
};
